import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import Pagination from '../../../components/common/Pagination';

const LeadDetail = () => {
    const { id } = useParams();
    const [lead, setLead] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Pagination states
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const [filters, setFilters] = useState({
        company_name: '',
        email: '',
        phone: '',
        status: ''
    });

    const fetchLeadVendors = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                ...filters,
                page: page,
                limit: 10
            });
            const response = await superAdminApiClient.get(`/superadmin/leads/${id}/vendors?${queryParams}`);
            setLead(response.data.lead);
            setVendors(response.data.vendors);
            setTotalPages(response.data.total_pages);
            setTotalRecords(response.data.total_records);
        } catch (error) {
            console.error('Error fetching lead details and vendors', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            if (id) {
                fetchLeadVendors();
            }
        }, 500);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, filters, page]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
        setPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const queryParams = new URLSearchParams(filters);
            const response = await superAdminApiClient.get(`/superadmin/leads/${id}/export-vendors?${queryParams}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `lead_${id}_vendors.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Export failed", err);
            alert("Export failed!");
        } finally {
            setExporting(false);
        }
    };

    return (
        <React.Fragment>
            <div className="row mb-3">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">
                            <Link to="/superadmin/leads" className="text-primary me-2"><i className="mdi mdi-arrow-left"></i></Link>
                            Lead Details & Mapped Vendors
                        </h4>
                        <div>
                            <button className="btn btn-success" onClick={handleExport} disabled={exporting || !lead}>
                                {exporting ? 'Exporting...' : 'Export mapped vendors'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lead Info Card */}
            {lead && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5>Lead Information</h5>
                                <div className="row">
                                    <div className="col-md-3"><strong>Name:</strong> {lead.name}</div>
                                    <div className="col-md-3"><strong>Phone:</strong> {lead.mobile}</div>
                                    <div className="col-md-3"><strong>Email:</strong> {lead.email || 'N/A'}</div>
                                    <div className="col-md-3"><strong>Category:</strong> <span className="badge bg-info">{lead.category}</span></div>
                                    <div className="col-md-3"><strong>City:</strong> {lead.city || 'N/A'}</div>
                                    <div className="col-md-3"><strong>Source:</strong> {lead.source || 'N/A'}</div>
                                </div>
                                {lead.message && (
                                    <div className="row mt-3">
                                        <div className="col-12">
                                            <strong>Message:</strong>
                                            <p className="mt-1 p-2 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>{lead.message}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Vendor List Filters */}
            <h5 className="mb-3">Assigned Vendors</h5>
            <div className="row mb-3">
                <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="Vendor Company" name="company_name" value={filters.company_name} onChange={handleFilterChange} />
                </div>
                <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="Vendor Phone" name="phone" value={filters.phone} onChange={handleFilterChange} />
                </div>
                <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="Vendor Email" name="email" value={filters.email} onChange={handleFilterChange} />
                </div>
                <div className="col-md-3">
                    <select className="form-select" name="status" value={filters.status} onChange={handleFilterChange}>
                        <option value="">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="follow_up">Follow-up</option>
                        <option value="closed">Closed</option>
                        <option value="converted">Converted</option>
                    </select>
                </div>
            </div>

            {/* Vendor Details */}
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loading && !vendors.length ? (
                                <div>Loading mappings...</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Vendor Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>WhatsApp Sent</th>
                                                <th>Email Sent</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {vendors.length > 0 ? vendors.map((vendor) => (
                                                <tr key={vendor.id}>
                                                    <td>{vendor.company_name}</td>
                                                    <td>{vendor.email}</td>
                                                    <td>{vendor.phone}</td>
                                                    <td>
                                                        {vendor.sent_whatsapp ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}
                                                    </td>
                                                    <td>
                                                        {vendor.sent_email ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${vendor.status === 'converted' ? 'bg-success' : vendor.status === 'closed' ? 'bg-danger' : vendor.status === 'follow_up' ? 'bg-warning' : 'bg-info'}`}>
                                                            {(vendor.status || 'open').replace('_', ' ').toUpperCase()}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="5" className="text-center">No vendors assigned to this lead.</td></tr>
                                            )}
                                        </tbody>
                                    </table>

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing {vendors.length} of {totalRecords} vendors
                                        </div>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={(p) => setPage(p)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default LeadDetail;
